export type TCountry = 'us' | 'ca'
export type TCountryLanguage<T extends TCountry> = {
  readonly us: readonly ['en']
  readonly ca: readonly ['en', 'fr']
}[T][number]

export const ECOUNTRIES: Record<TCountry, TCountry> = {
  us: 'us',
  ca: 'ca',
}

export enum COUNTRY_CODE {
  US = 'us',
  CA = 'ca',
}
export enum LANGUAGE_CODE {
  EN = 'en',
  FR = 'fr',
}

export const SUPPORTED_COUNTRIES = ['us', 'ca'] as const
export const COUNTRY_LANGUAGES = {
  us: ['en'] as const,
  ca: ['en', 'fr'] as const,
} as const

export const LOCALE_COOKIE = 'NEXT_PREFERRED_LOCALE'
export const REGION_DETAILS_LOCAL_STORAGE_KEY = 'REGION_DETAILS'
// @i18n-ignore
export const DEFAULT_LOCALE = 'default-locale'
// @i18n-ignore
export const DEFAULT_LANGUAGE = 'en-us' // TODO(sachin_i18n): get language from Next preferred cookie
// @i18n-ignore
export const LOCALE_FORMAT = '{{language}}-{{country}}'
export const DEFAULT_COUNTRY: TCountry = SUPPORTED_COUNTRIES[0]
export const DEFAULT_LOCALE_PREFIX = 'en-us'

// @i18n-ignore
export const COMMON_NAMESPACES = ['common', 'pdp', 'landing', 'accountPage'] as const
export type TNamespace = (typeof COMMON_NAMESPACES)[number]

export interface TLocalisationDataLocalStorage<T extends TCountryStandard = TCountryStandard> {
  regionId?: number
  locale: TAbsoluteLocaleStandard
  country: T
  language: TCountryLanguageStandard<T>
  currency: string
}

type TLocaleFormat = typeof LOCALE_FORMAT

// TAbsoluteLocale is specific for Quince
// It is used to match/identify the locale of the user
// The locale is in lowercase
// For any Server Side Rendering, we need to use the TAbsoluteLocaleStandard locale
// For any API calls, we need to use the TAbsoluteLocaleStandard locale

export type TAbsoluteLocale = {
  [K in TCountry]: TLocaleFormat extends `${infer Prefix}{{country}}${infer Middle}{{language}}${infer Suffix}`
    ? `${Prefix}${K}${Middle}${(typeof COUNTRY_LANGUAGES)[K][number]}${Suffix}`
    : TLocaleFormat extends `${infer Prefix}{{language}}${infer Middle}{{country}}${infer Suffix}`
      ? `${Prefix}${(typeof COUNTRY_LANGUAGES)[K][number]}${Middle}${K}${Suffix}`
      : never
}[TCountry]

// Types for standard
export type TCountryStandard = Uppercase<TCountry>
export const LOCALE_FORMAT_STANDARD = '{{language}}-{{countryUpper}}'
type TLocaleFormatStandard = typeof LOCALE_FORMAT_STANDARD
export type TCountryLanguageStandard<T extends TCountryStandard> = TCountryLanguage<Lowercase<T> & TCountry>

// Now create the uppercase absolute locale type
export type TAbsoluteLocaleStandard = {
  [K in TCountry]: TLocaleFormatStandard extends `${infer Prefix}{{countryUpper}}${infer Middle}{{language}}${infer Suffix}`
    ? `${Prefix}${Uppercase<K>}${Middle}${(typeof COUNTRY_LANGUAGES)[K][number]}${Suffix}`
    : TLocaleFormatStandard extends `${infer Prefix}{{language}}${infer Middle}{{countryUpper}}${infer Suffix}`
      ? `${Prefix}${(typeof COUNTRY_LANGUAGES)[K][number]}${Middle}${Uppercase<K>}${Suffix}`
      : never
}[TCountry]

// Add a formatter for the standard format
export const formatLocaleStandard = <T extends TCountry>(
  country: T,
  language: TCountryLanguage<T>,
): TAbsoluteLocaleStandard =>
  // eslint-disable-next-line i18next/no-literal-string
  LOCALE_FORMAT_STANDARD.replace('{{countryUpper}}', country.toUpperCase()).replace('{{language}}', language) as TAbsoluteLocaleStandard // prettier-ignore

// Add standard absolute locales
export const ABSOLUTE_LOCALES_STANDARD = SUPPORTED_COUNTRIES.flatMap(country =>
  COUNTRY_LANGUAGES[country].map(language => formatLocaleStandard(country, language)),
)

export const formatLocale = <T extends TCountry>(country: T, language: TCountryLanguage<T>): TAbsoluteLocale =>
  // eslint-disable-next-line i18next/no-literal-string
  LOCALE_FORMAT.replace('{{country}}', country).replace('{{language}}', language) as TAbsoluteLocale

export const FALLBACK_LOCALE = formatLocale(DEFAULT_COUNTRY, COUNTRY_LANGUAGES[DEFAULT_COUNTRY][0])

export const FALLBACK_REGION_ID = 101

export const ABSOLUTE_LOCALES = SUPPORTED_COUNTRIES.flatMap(country =>
  COUNTRY_LANGUAGES[country].map(language => formatLocale(country, language)),
)

export type TAllLocale = typeof DEFAULT_LOCALE | TCountry | TAbsoluteLocale
export const ALL_LOCALES = [DEFAULT_LOCALE, ...SUPPORTED_COUNTRIES, ...ABSOLUTE_LOCALES] as TAllLocale[]
export interface LocaleInfo {
  country: TCountry
  language: TCountryLanguage<TCountry>
}
